import React, {
  useEffect,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


const CALENDAR_PRESETS = [
  {
    value: 1,
    label: "Mensuelle",
    description: "Maintenance mensuelle",
    warningDays: 7,
  },
  {
    value: 3,
    label: "Trimestrielle",
    description: "Maintenance trimestrielle",
    warningDays: 21,
  },
  {
    value: 6,
    label: "Semestrielle",
    description: "Maintenance semestrielle",
    warningDays: 30,
  },
  {
    value: 9,
    label: "9 mois",
    description: "Maintenance tous les 9 mois",
    warningDays: 45,
  },
  {
    value: 12,
    label: "Annuelle",
    description: "Maintenance annuelle",
    warningDays: 60,
  },
];


function calendarPresetName(months) {
  const labels = {
    1: "Maintenance préventive mensuelle",
    3: "Maintenance préventive trimestrielle",
    6: "Maintenance préventive semestrielle",
    9: "Maintenance préventive 9 mois",
    12: "Maintenance préventive annuelle",
  };

  return labels[months] || "";
}


function toDateInput(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date
    .toISOString()
    .slice(0, 10);
}


function initialForm(
  plan,
  machineId
) {
  return {
    machine_id:
      plan?.machineId ||
      machineId ||
      "",
    name:
      plan?.name || "",
    description:
      plan?.description || "",
    schedule_type:
      plan?.scheduleType ||
      "calendar",
    interval_value:
      plan?.intervalValue ?? 1,
    interval_unit:
      plan?.intervalUnit ||
      "month",
    warning_value:
      plan?.warningValue ?? 30,
    first_due_at:
      toDateInput(
        plan?.nextDueAt
      ),
    first_due_meter_hours:
      plan?.nextDueMeterHours ??
      "",
    priority:
      plan?.priority ||
      "normal",
    default_duration_minutes:
      plan?.defaultDurationMinutes ??
      60,
  };
}


export default function MaintenancePlanModal({
  open,
  plan = null,
  machines = [],
  initialMachineId = "",
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] =
    useState(
      initialForm(
        plan,
        initialMachineId
      )
    );


  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(
      initialForm(
        plan,
        initialMachineId
      )
    );
  }, [
    initialMachineId,
    open,
    plan,
  ]);


  if (!open) {
    return null;
  }


  const update = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const changeScheduleType = (
    value
  ) => {
    setForm((current) => ({
      ...current,
      schedule_type: value,
      interval_unit:
        value === "meter"
          ? "hour"
          : current.interval_unit ===
              "hour"
            ? "month"
            : current.interval_unit,
      warning_value:
        value === "meter"
          ? 50
          : 30,
    }));
  };


  const applyCalendarPreset = (
    preset
  ) => {
    setForm((current) => {
      const currentAutomaticName =
        calendarPresetName(
          Number(
            current.interval_value
          )
        );

      const shouldReplaceName =
        !current.name.trim() ||
        current.name ===
          currentAutomaticName;

      return {
        ...current,
        schedule_type: "calendar",
        interval_value:
          preset.value,
        interval_unit: "month",
        warning_value:
          preset.warningDays,
        name:
          shouldReplaceName
            ? calendarPresetName(
                preset.value
              )
            : current.name,
      };
    });
  };


  const submit = (
    event
  ) => {
    event.preventDefault();

    if (!form.machine_id) {
      toast.error(
        "Sélectionnez une machine."
      );

      return;
    }

    if (!form.name.trim()) {
      toast.error(
        "Renseignez le nom du plan."
      );

      return;
    }

    if (
      Number(
        form.interval_value
      ) <= 0
    ) {
      toast.error(
        "La périodicité doit être supérieure à zéro."
      );

      return;
    }

    onSubmit({
      ...(
        plan
          ? {}
          : {
              machine_id:
                form.machine_id,
            }
      ),
      name: form.name.trim(),
      description:
        form.description.trim(),
      schedule_type:
        form.schedule_type,
      interval_value:
        Number(
          form.interval_value
        ),
      interval_unit:
        form.schedule_type ===
        "meter"
          ? "hour"
          : form.interval_unit,
      warning_value:
        Number(
          form.warning_value
        ),
      first_due_at:
        form.schedule_type ===
          "calendar" &&
        form.first_due_at
          ? form.first_due_at
          : null,
      first_due_meter_hours:
        form.schedule_type ===
          "meter" &&
        form.first_due_meter_hours !==
          ""
          ? Number(
              form.first_due_meter_hours
            )
          : null,
      priority:
        form.priority,
      default_duration_minutes:
        Number(
          form.default_duration_minutes
        ),
    });
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60";


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--do-overlay)] p-4"
      data-testid="maintenance-plan-modal"
    >
      <form
        onSubmit={submit}
        className="acta-modal-surface max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-[var(--do-bg-solid)] p-5 shadow-2xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow">
              Maintenance préventive
            </div>

            <h2 className="mt-1 font-heading text-xl font-black">
              {plan
                ? "Modifier le plan"
                : "Nouveau plan d’entretien"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fermer"
            className="rounded-lg border border-[var(--do-border)] p-2"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">
              Machine
            </label>

            <select
              value={
                form.machine_id
              }
              onChange={(event) =>
                update(
                  "machine_id",
                  event.target.value
                )
              }
              disabled={
                saving ||
                Boolean(plan)
              }
              data-testid="maintenance-plan-machine"
              className={inputClass}
            >
              <option value="">
                Sélectionner une machine
              </option>

              {machines.map(
                (machine) => (
                  <option
                    key={machine.id}
                    value={machine.id}
                  >
                    {machine.name}
                    {" — "}
                    {machine.serialNumber}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">
              Nom du plan
            </label>

            <input
              type="text"
              value={form.name}
              onChange={(event) =>
                update(
                  "name",
                  event.target.value
                )
              }
              disabled={saving}
              maxLength={180}
              data-testid="maintenance-plan-name"
              className={inputClass}
              placeholder="Entretien annuel, vidange, contrôle sécurité…"
            />
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">
              Description
            </label>

            <textarea
              value={
                form.description
              }
              onChange={(event) =>
                update(
                  "description",
                  event.target.value
                )
              }
              disabled={saving}
              rows={4}
              maxLength={4000}
              className={`${inputClass} resize-y`}
              placeholder="Opérations à réaliser, points de contrôle, consignes…"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label-eyebrow mb-1.5 block">
                Déclenchement
              </label>

              <select
                value={
                  form.schedule_type
                }
                onChange={(event) =>
                  changeScheduleType(
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="maintenance-plan-schedule-type"
                className={inputClass}
              >
                <option value="calendar">
                  Périodicité calendaire
                </option>

                <option value="meter">
                  Compteur d’heures
                </option>
              </select>
            </div>

            <div>
              <label className="label-eyebrow mb-1.5 block">
                Priorité
              </label>

              <select
                value={
                  form.priority
                }
                onChange={(event) =>
                  update(
                    "priority",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                <option value="low">
                  Basse
                </option>
                <option value="normal">
                  Normale
                </option>
                <option value="high">
                  Haute
                </option>
                <option value="urgent">
                  Urgente
                </option>
              </select>
            </div>
          </div>

          {form.schedule_type ===
            "calendar" && (
            <div>
              <div className="label-eyebrow mb-2">
                Fréquence rapide
              </div>

              <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                data-testid="maintenance-calendar-presets"
              >
                {CALENDAR_PRESETS.map(
                  (preset) => {
                    const selected =
                      form.interval_unit ===
                        "month" &&
                      Number(
                        form.interval_value
                      ) === preset.value;

                    return (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() =>
                          applyCalendarPreset(
                            preset
                          )
                        }
                        disabled={saving}
                        aria-pressed={
                          selected
                        }
                        data-testid={
                          `maintenance-preset-${preset.value}-months`
                        }
                        className={[
                          "rounded-xl border p-3 text-left transition",
                          selected
                            ? "border-[var(--do-accent)] bg-[var(--do-surface-soft)] ring-1 ring-[var(--do-accent)]"
                            : "border-[var(--do-border)] hover:bg-[var(--do-surface-soft)]",
                        ].join(" ")}
                      >
                        <div className="font-heading text-sm font-bold">
                          {preset.label}
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {preset.description}
                        </div>

                        <div className="mt-2 text-[11px] text-[var(--do-muted)]">
                          Alerte{" "}
                          {preset.warningDays}
                          {" "}jour(s) avant
                        </div>
                      </button>
                    );
                  }
                )}
              </div>

              <p className="mt-2 text-xs text-[var(--do-muted)]">
                Vous pouvez toujours saisir une fréquence personnalisée ci-dessous.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">
                Tous les
              </label>

              <input
                type="number"
                min="0.01"
                step={
                  form.schedule_type ===
                  "calendar"
                    ? "1"
                    : "0.01"
                }
                value={
                  form.interval_value
                }
                onChange={(event) =>
                  update(
                    "interval_value",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="maintenance-plan-interval"
                className={inputClass}
              />
            </div>

            <div>
              <label className="label-eyebrow mb-1.5 block">
                Unité
              </label>

              <select
                value={
                  form.schedule_type ===
                  "meter"
                    ? "hour"
                    : form.interval_unit
                }
                onChange={(event) =>
                  update(
                    "interval_unit",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  form.schedule_type ===
                    "meter"
                }
                className={inputClass}
              >
                {form.schedule_type ===
                "meter" ? (
                  <option value="hour">
                    Heures
                  </option>
                ) : (
                  <>
                    <option value="day">
                      Jours
                    </option>
                    <option value="week">
                      Semaines
                    </option>
                    <option value="month">
                      Mois
                    </option>
                    <option value="year">
                      Années
                    </option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="label-eyebrow mb-1.5 block">
                Préalerte
              </label>

              <input
                type="number"
                min="0"
                step="1"
                value={
                  form.warning_value
                }
                onChange={(event) =>
                  update(
                    "warning_value",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />

              <div className="mt-1 text-[11px] text-[var(--do-muted)]">
                {form.schedule_type ===
                "meter"
                  ? "Heures avant échéance"
                  : "Jours avant échéance"}
              </div>
            </div>
          </div>

          {form.schedule_type ===
          "calendar" ? (
            <div>
              <label className="label-eyebrow mb-1.5 block">
                Première / prochaine échéance
              </label>

              <input
                type="date"
                value={
                  form.first_due_at
                }
                onChange={(event) =>
                  update(
                    "first_due_at",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="maintenance-plan-first-due"
                className={inputClass}
              />
            </div>
          ) : (
            <div>
              <label className="label-eyebrow mb-1.5 block">
                Prochaine échéance compteur
              </label>

              <input
                type="number"
                min="0"
                step="0.01"
                value={
                  form.first_due_meter_hours
                }
                onChange={(event) =>
                  update(
                    "first_due_meter_hours",
                    event.target.value
                  )
                }
                disabled={saving}
                data-testid="maintenance-plan-first-meter"
                className={inputClass}
                placeholder="Exemple : 1500"
              />
            </div>
          )}

          <div>
            <label className="label-eyebrow mb-1.5 block">
              Durée prévisionnelle
            </label>

            <input
              type="number"
              min="1"
              max="10080"
              value={
                form.default_duration_minutes
              }
              onChange={(event) =>
                update(
                  "default_duration_minutes",
                  event.target.value
                )
              }
              disabled={saving}
              className={inputClass}
            />

            <div className="mt-1 text-[11px] text-[var(--do-muted)]">
              Durée en minutes utilisée lors de la génération de l’intervention.
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-[var(--do-border)] pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-heading font-semibold"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            data-testid="maintenance-plan-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
          >
            <Check
              size={17}
              weight="bold"
            />

            {saving
              ? "Enregistrement…"
              : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
